import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { TemplateFieldType, TemplatePrefill, type TemplateInput } from "shared";
import { api } from "../../api";

type Summary = { id: string; programSlug: string; programName: string; stateCode: string | null; version: number; status: string; title: string; updatedAt: string };
type Detail = Summary & { instructions: string; fields: TemplateInput["fields"]; sourceUrl: string | null };
/** One editable field row; options is the raw comma list. */
type Row = { key: string; label: string; type: string; required: boolean; help: string; officialFieldRef: string; options: string; prefill: string; sensitive: boolean };

const STATUS_LABEL: Record<string, string> = { draft: "Draft", published: "Published", retired: "Retired" };
const PREFILL_LABEL: Record<string, string> = { "customer.name": "Name", "customer.phone": "Phone", "customer.email": "Email", "customer.state": "State", "customer.zip": "ZIP" };
const blankRow = (): Row => ({ key: "", label: "", type: "text", required: false, help: "", officialFieldRef: "", options: "", prefill: "", sensitive: false });

const toRow = (f: Detail["fields"][number]): Row => ({ key: f.key, label: f.label, type: f.type, required: f.required, help: f.help ?? "", officialFieldRef: f.officialFieldRef ?? "",
  options: (f.options ?? []).join(", "), prefill: f.prefill ?? "", sensitive: f.sensitiveEnterOnOfficialSite ?? false });

function toField(r: Row): TemplateInput["fields"][number] {
  const f: Record<string, unknown> = { key: r.key.trim(), label: r.label.trim(), type: r.type, required: r.sensitive ? false : r.required };
  if (r.help.trim()) f.help = r.help.trim();
  if (r.officialFieldRef.trim()) f.officialFieldRef = r.officialFieldRef.trim();
  if (r.type === "select") f.options = r.options.split(",").map((o) => o.trim()).filter(Boolean);
  if (r.prefill && !r.sensitive) f.prefill = r.prefill;
  if (r.sensitive) f.sensitiveEnterOnOfficialSite = true;
  return f as TemplateInput["fields"][number];
}

function Editor({ id, onOpen, onClose }: { id: string; onOpen: (id: string) => void; onClose: () => void }) {
  const qc = useQueryClient();
  const isNew = id === "new";
  const programs = useQuery({ queryKey: ["rules-programs"], queryFn: () => api<{ programs: { slug: string; name: string }[] }>("/api/admin/rules"), enabled: isNew });
  const tpl = useQuery({ queryKey: ["template", id], enabled: !isNew, queryFn: () => api<Detail>(`/api/admin/templates/${id}`) });
  const [programSlug, setProgramSlug] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [rows, setRows] = useState<Row[]>([blankRow()]);
  const [msg, setMsg] = useState({ error: "", info: "", busy: false });

  const load = (t: Detail) => {
    setProgramSlug(t.programSlug); setStateCode(t.stateCode ?? ""); setTitle(t.title); setInstructions(t.instructions);
    setSourceUrl(t.sourceUrl ?? ""); setRows(t.fields.map(toRow));
  };
  useEffect(() => { if (tpl.data) load(tpl.data); }, [tpl.data]);

  const status = isNew ? "draft" : tpl.data?.status;
  const readOnly = status !== "draft";
  if (!isNew && tpl.error) return <p role="alert" className="text-sm text-red-600">{(tpl.error as Error).message}</p>;
  if (!isNew && !tpl.data) return <p className="text-sm text-slate-500">Loading</p>;

  const patch = (i: number, p: Partial<Row>) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...p } : r)));
  const move = (i: number, d: -1 | 1) => setRows((rs) => {
    const j = i + d;
    if (j < 0 || j >= rs.length) return rs;
    const n = [...rs];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const body = (): TemplateInput => {
    const b: Record<string, unknown> = { programSlug, stateCode: stateCode.trim() ? stateCode.trim().toUpperCase() : null, title: title.trim(), instructions, fields: rows.map(toField) };
    if (sourceUrl.trim()) b.sourceUrl = sourceUrl.trim();
    return b as TemplateInput;
  };
  const run = async (fn: () => Promise<Detail | undefined>, info: string, after?: (t: Detail) => void) => {
    setMsg({ error: "", info: "", busy: true });
    try {
      const t = await fn();
      qc.invalidateQueries({ queryKey: ["templates"] });
      qc.invalidateQueries({ queryKey: ["template"] });
      if (t) { qc.setQueryData(["template", t.id], t); after ? after(t) : load(t); }
      setMsg({ error: "", info, busy: false });
    } catch (e) { setMsg({ error: (e as Error).message, info: "", busy: false }); }
  };
  const post = (suffix: string) => api<Detail>(`/api/admin/templates/${id}/${suffix}`, { method: "POST" });
  const put = () => api<Detail>(`/api/admin/templates/${id}`, { method: "PUT", body: body() });
  const save = () => run(() => isNew ? api<Detail>("/api/admin/templates", { method: "POST", body: body() }) : put(),
    "Saved", isNew ? (t) => onOpen(t.id) : undefined);
  /** Publishes what is on screen: the form is saved first, and a failed save stops the publish. */
  const publish = () => run(async () => { await put(); return post("publish"); }, "Saved and published");

  return (
    <section className="card flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">{isNew ? "New template" : `${tpl.data!.programName} version ${tpl.data!.version}`}
          {!isNew && <span className="ml-2 rounded border border-slate-300 px-1.5 py-0.5 text-xs">Status: {STATUS_LABEL[status!] ?? status}</span>}</h2>
        <button type="button" className="btn-ghost" onClick={onClose}>Back to list</button>
      </div>
      {readOnly && <p className="text-sm text-slate-500">This template is {status}. It cannot be edited; create a new version to make changes.</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="tpl-program">Program</label>
          {isNew ? (
            <select id="tpl-program" className="input" value={programSlug} onChange={(e) => setProgramSlug(e.target.value)}>
              <option value="">Choose a program</option>
              {(programs.data?.programs ?? []).map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
            </select>
          ) : <input id="tpl-program" className="input" disabled value={tpl.data!.programName} />}
        </div>
        <div>
          <label className="label" htmlFor="tpl-state">State (blank = national)</label>
          <input id="tpl-state" className="input" maxLength={2} disabled={!isNew} value={stateCode} onChange={(e) => setStateCode(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor="tpl-title">Title</label>
          <input id="tpl-title" className="input" disabled={readOnly} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor="tpl-instructions">Instructions</label>
          <textarea id="tpl-instructions" className="input" disabled={readOnly} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor="tpl-source">Source URL (https)</label>
          <input id="tpl-source" className="input" disabled={readOnly} value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} />
        </div>
      </div>

      <h3 className="text-sm font-semibold">Fields</h3>
      {rows.map((r, i) => (
        <div key={i} className="flex items-start gap-2 rounded-md border border-slate-200 p-2 dark:border-slate-800">
          <div className="grid flex-1 gap-2 sm:grid-cols-2">
            <div><label className="label" htmlFor={`f-${i}-key`}>Key</label>
              <input id={`f-${i}-key`} className="input" disabled={readOnly} value={r.key} onChange={(e) => patch(i, { key: e.target.value })} /></div>
            <div><label className="label" htmlFor={`f-${i}-label`}>Label</label>
              <input id={`f-${i}-label`} className="input" disabled={readOnly} value={r.label} onChange={(e) => patch(i, { label: e.target.value })} /></div>
            <div><label className="label" htmlFor={`f-${i}-type`}>Type</label>
              <select id={`f-${i}-type`} className="input" disabled={readOnly} value={r.type} onChange={(e) => patch(i, { type: e.target.value })}>
                {TemplateFieldType.options.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label className="label" htmlFor={`f-${i}-help`}>Help</label>
              <input id={`f-${i}-help`} className="input" disabled={readOnly} value={r.help} onChange={(e) => patch(i, { help: e.target.value })} /></div>
            <div><label className="label" htmlFor={`f-${i}-ref`}>Official field ref</label>
              <input id={`f-${i}-ref`} className="input" disabled={readOnly} value={r.officialFieldRef} onChange={(e) => patch(i, { officialFieldRef: e.target.value })} /></div>
            {r.type === "select" && (
              <div><label className="label" htmlFor={`f-${i}-options`}>Options (comma separated)</label>
                <input id={`f-${i}-options`} className="input" disabled={readOnly} value={r.options} onChange={(e) => patch(i, { options: e.target.value })} /></div>
            )}
            <div><label className="label" htmlFor={`f-${i}-prefill`}>Prefill from customer</label>
              <select id={`f-${i}-prefill`} className="input" disabled={readOnly || r.sensitive} value={r.prefill} onChange={(e) => patch(i, { prefill: e.target.value })}>
                <option value="">None</option>
                {TemplatePrefill.options.map((p) => <option key={p} value={p}>{PREFILL_LABEL[p] ?? p}</option>)}
              </select></div>
            <label className="flex items-center gap-2 text-sm" htmlFor={`f-${i}-required`}>
              <input id={`f-${i}-required`} type="checkbox" disabled={readOnly || r.sensitive} checked={r.required} onChange={(e) => patch(i, { required: e.target.checked })} />Required</label>
            <label className="flex items-center gap-2 text-sm" htmlFor={`f-${i}-sensitive`}>
              <input id={`f-${i}-sensitive`} type="checkbox" disabled={readOnly} checked={r.sensitive}
                onChange={(e) => patch(i, e.target.checked ? { sensitive: true, required: false, prefill: "" } : { sensitive: false })} />Enter on the official site (no value stored)</label>
          </div>
          {!readOnly && (
            <div className="flex flex-col">
              <button type="button" className="btn-ghost px-1" aria-label={`Move up field ${i + 1}`} onClick={() => move(i, -1)}><ArrowUp size={14} /></button>
              <button type="button" className="btn-ghost px-1" aria-label={`Move down field ${i + 1}`} onClick={() => move(i, 1)}><ArrowDown size={14} /></button>
              <button type="button" className="btn-ghost px-1 text-red-600" aria-label={`Remove field ${i + 1}`} onClick={() => setRows(rows.filter((_, k) => k !== i))}><Trash2 size={14} /></button>
            </div>
          )}
        </div>
      ))}
      {!readOnly && <div><button type="button" className="btn-ghost" onClick={() => setRows([...rows, blankRow()])}><Plus size={14} /> Add field</button></div>}

      <div className="flex flex-wrap items-center gap-2">
        {status === "draft" && <button type="button" className="btn-primary" disabled={msg.busy} onClick={save}>{isNew ? "Create draft" : "Save draft"}</button>}
        {status === "draft" && !isNew && <button type="button" className="btn-ghost" disabled={msg.busy} onClick={publish}>Publish</button>}
        {status === "published" && <button type="button" className="btn-ghost" disabled={msg.busy} onClick={() => run(() => post("retire"), "Retired")}>Retire</button>}
        {!isNew && readOnly && <button type="button" className="btn-primary" disabled={msg.busy} onClick={() => run(() => post("new-version"), "New draft created", (t) => onOpen(t.id))}>Create new version</button>}
        {msg.info && <span role="status" className="text-sm text-emerald-700">{msg.info}</span>}
      </div>
      <div role="alert" className="text-sm text-red-600">{msg.error}</div>
    </section>
  );
}

export function TemplatesAdmin() {
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useQuery({ queryKey: ["templates"], queryFn: () => api<{ templates: Summary[] }>("/api/admin/templates") });
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4">
      <section className="card">
        <div className="mb-2 flex items-center justify-between">
          <h1 className="text-lg font-semibold">Worksheet templates</h1>
          <button type="button" className="btn-primary" onClick={() => setOpenId("new")}>New template</button>
        </div>
        <p className="mb-3 text-sm text-slate-500">A published template is read-only. To change it, create a new version, edit the draft and publish it.</p>
        {list.error && <p role="alert" className="text-sm text-red-600">{(list.error as Error).message}</p>}
        <ul className="flex flex-col gap-1">
          {(list.data?.templates ?? []).map((t) => (
            <li key={t.id}>
              <button type="button" className="flex w-full items-center gap-3 rounded-md border border-slate-200 p-2 text-left text-sm dark:border-slate-800" onClick={() => setOpenId(t.id)}>
                <span className="w-40 shrink-0">{t.programName}</span>
                <span className="w-16 shrink-0">{t.stateCode ?? "National"}</span>
                <span className="w-8 shrink-0">v{t.version}</span>
                <span className="w-24 shrink-0 rounded border border-slate-300 px-1.5 text-xs">{STATUS_LABEL[t.status] ?? t.status}</span>
                <span className="flex-1">{t.title}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
      {openId && <Editor key={openId} id={openId} onOpen={setOpenId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
